import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Badge,
  Button,
  Divider,
  Field,
  Input,
  Tooltip,
  Text,
  Spinner,
  useToastController,
  Toast,
  ToastTitle,
} from '@coreai/fluentui-react';
import {
  DismissRegular,
  EditRegular,
  CheckmarkRegular,
  PlayRegular,
  SparkleRegular,
  LockClosedRegular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { NodeEditorAutosaveIndicator } from './shared/NodeEditorAutosaveIndicator';
import { useNodeConnectorInfo, getNodeActionName as getEditableName } from '@/hooks/useNodeConnectorInfo';
import { isAgentAction } from '@/types/workflowDefinition';
import { getBuiltInManifest } from '@/data/manifests';
import { MAX_NAME_LENGTH } from '@/utils/validation';
import type { Action } from '@/types/workflowDefinition';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  AgentVariantIcon,
  getAgentBadgeTokens,
  getAgentVariant,
  getAgentVariantLabel,
  useAgentIconForegroundClass,
} from '../helpers/agentVariant';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    // Fixed height so toggling between display and edit modes never shifts
    // the rest of the panel.
    height: '64px',
    flexShrink: 0,
  },

  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    flexShrink: 0,
    overflow: 'hidden',
  },
  connectorIcon: {
    width: '22px',
    height: '22px',
    objectFit: 'contain',
  },
  defaultIcon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground2,
  },
  agentIcon: {
    fontSize: tokens.fontSizeBase500,
  },

  titleSection: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    // Use no gap; let each row carry its own line-height for cleaner optical rhythm.
  },
  eyebrow: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    // Locked to the input "medium" height so the header doesn't grow when
    // toggling between display and edit modes.
    minHeight: '32px',
  },
  // Pencil is muted by default so the name dominates; lifts on hover/focus.
  editButton: {
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'color, background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  nodeName: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameButton: {
    // The name acts as a click target to start editing when not read-only.
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    cursor: 'text',
    minWidth: 0,
    color: 'inherit',
    font: 'inherit',
    // Tactile press feedback — subtle on heading-sized text.
    transitionProperty: 'transform, outline-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':active': {
      transform: 'scale(0.98)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  nameInput: {
    // Width sized to typical name length; never stretches the whole header.
    flex: '0 1 auto',
    width: '280px',
    maxWidth: '100%',
  },
  // Make the input read like editing the actual name text: same size + weight,
  // underline appearance with no chunky border. Height matches the medium
  // Input so the row doesn't grow when toggling into edit mode.
  nameInputControl: {
    '& input': {
      fontSize: tokens.fontSizeBase400,
      lineHeight: tokens.lineHeightBase400,
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground1,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  nameCounter: {
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground3,
    userSelect: 'none',
    pointerEvents: 'none',
  },
  nameCounterWarning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  nameCounterDanger: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  readOnlyBadge: {
    flexShrink: 0,
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  actionsDivider: {
    height: '20px',
    marginLeft: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXXS,
  },
});

const NAME_SOFT_CAP = 50;
const NAME_HARD_CAP = MAX_NAME_LENGTH;
// Show the live counter once the user is within ~25% of the hard cap,
// not from the first keystroke — keeps the input chrome clean for the
// common case (short names).
const NAME_COUNTER_REVEAL = Math.floor(NAME_HARD_CAP * 0.75);
const NAME_LONG_WARNING = 'Long names truncate on the canvas.';

// Build the Field validation props for the name input. Returns an empty
// object when there's nothing to surface so the spread is a no-op.
function getNameValidationProps(
  error: string,
  nameLength: number,
):
  | { validationState: 'error'; validationMessage: string }
  | { validationState: 'warning'; validationMessage: string }
  | Record<string, never> {
  if (error) return { validationState: 'error', validationMessage: error };
  if (nameLength > NAME_SOFT_CAP) {
    return { validationState: 'warning', validationMessage: NAME_LONG_WARNING };
  }
  return {};
}

// Pick the colour class for the live character counter based on how close
// the value is to the hard cap. Returns undefined under the soft cap so
// the counter stays neutral.
function getNameCounterClass(
  styles: { nameCounterDanger: string; nameCounterWarning: string },
  nameLength: number,
): string | undefined {
  if (nameLength >= NAME_HARD_CAP) return styles.nameCounterDanger;
  if (nameLength > NAME_SOFT_CAP) return styles.nameCounterWarning;
  return undefined;
}

/**
 * NodeEditorHeader — header for the node editor surface (modal or drawer).
 *
 * `variant` reshapes only the presentation, never the logic:
 *  - `modal` (legacy centered dialog) and `drawer` (side panel) share the
 *    same header layout: a colored icon tile (32px), a two-line title
 *    section (eyebrow above name, name is 16px semibold and inline-editable),
 *    autosave indicator, ask-Copilot button, and window controls.
 *  - Eyebrow content:
 *      * Agents → the variant label ("Foundry Agent", "Managed Agent", …).
 *      * Everything else → the category suffix ("Trigger details" /
 *        "Tool details" / "Action details"). This mirrors the modal's
 *        original eyebrow copy and avoids leaking raw internal type enums
 *        like "Http", "Compose", or "Foreach" into the UI.
 *
 * Accessibility:
 *  - The icon tile is decorative — `aria-hidden="true"`, no `role="img"`,
 *    no `aria-label`. Screen readers hear the eyebrow (variant label or
 *    category) followed by the node name, once. Sighted mouse users still
 *    get a Tooltip on hover for the specific operation name.
 */
interface NodeEditorHeaderProps {
  variant?: 'modal' | 'drawer';
}

export const NodeEditorHeader = ({
  variant = 'modal',
}: NodeEditorHeaderProps = {}) => {
  const styles = useStyles();
  const agentIconForeground = useAgentIconForegroundClass();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');
  const [imageError, setImageError] = useState(false);
  const { dispatchToast } = useToastController();

  const {
    node,
    closeEditor,
    setNodeName,
    setToolName,
    setInnerActionName,
  } = useNodeEditorStore();
  const isReadOnly = useNodeEditorStore((s) => s.isReadOnly);
  const hasUnappliedNodeCode = useNodeEditorStore(
    (state) => state.nodeCodeDraft?.isDirty ?? false,
  );
  const isNameLocked = isReadOnly || hasUnappliedNodeCode;

  const renameAction = useWorkflowStore((s) => s.renameAction);
  const renameTrigger = useWorkflowStore((s) => s.renameTrigger);
  const renameTool = useWorkflowStore((s) => s.renameTool);
  const renameToolInnerAction = useWorkflowStore((s) => s.renameToolInnerAction);

  const { connectorInfo, connectionRef, isLoading } = useNodeConnectorInfo(node);
  const queuePrompt = useAgentChatAguiStore((s) => s.queuePrompt);
  const isStreaming = useAgentChatAguiStore((s) => s.isStreaming);
  const pendingPrompt = useAgentChatAguiStore((s) => s.pendingPrompt);
  const isAiBusy = isStreaming || pendingPrompt !== null;
  const isScopedEditEnabled = useFeatureFlag('scopedEdit');

  // Agents come in three flavors (native, Foundry, coding) that share
  // `type: 'Agent'` and the same manifest icon, so we use Fluent glyphs
  // (plus a custom coding glyph) + Otto agent badge tokens here to give
  // each variant a distinct color treatment.
  const agentAction =
    node?.type === 'action' && node.nodeData && isAgentAction(node.nodeData as Action)
      ? (node.nodeData as Action)
      : null;
  const isAgent = agentAction !== null;
  const agentVariant = agentAction ? getAgentVariant(agentAction) : null;

  // Built-in manifest fallback for non-agent operations without a connection.
  const builtInManifest = useMemo(() => {
    if (connectorInfo || isAgent) return null;
    if (!node?.nodeData?.type) return null;
    return getBuiltInManifest(node.nodeData.type);
  }, [connectorInfo, isAgent, node?.nodeData?.type]);

  const handleStartEdit = useCallback(() => {
    if (!node || isNameLocked) return;
    setEditName(getEditableName(node));
    setIsEditing(true);
  }, [isNameLocked, node]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditName('');
    setEditError('');
  }, []);

  useEffect(() => {
    if (hasUnappliedNodeCode && isEditing) {
      handleCancelEdit();
    }
  }, [handleCancelEdit, hasUnappliedNodeCode, isEditing]);

  const handleSaveEdit = useCallback(() => {
    if (hasUnappliedNodeCode) {
      setEditError('Apply or revert the code changes before renaming this node.');
      return;
    }
    if (!node || !editName.trim()) {
      handleCancelEdit();
      return;
    }

    // For tools, compare against inner action name; for others, compare against node name
    const currentName = getEditableName(node);

    // If name hasn't changed, just close
    if (editName.trim() === currentName) {
      handleCancelEdit();
      return;
    }

    // Call the appropriate rename function based on node type
    let result;
    if (node.type === 'trigger') {
      result = renameTrigger(node.name, editName.trim());
    } else if (node.type === 'tool' && node.parentAgentName && node.toolName && node.innerActionName) {
      result = renameToolInnerAction(node.parentAgentName, node.toolName, node.innerActionName, editName.trim());
    } else if (node.type === 'tool' && node.parentAgentName && node.toolName) {
      result = renameTool(node.parentAgentName, node.toolName, editName.trim());
    } else {
      result = renameAction(node.name, editName.trim());
    }

    if (!result.isValid) {
      setEditError(result.error ?? 'Invalid name');
      dispatchToast(
        <Toast>
          <ToastTitle>Rename failed: {result.error}</ToastTitle>
        </Toast>,
        { intent: 'error' }
      );
      return;
    }

    // Update the node editor's reference to the new name
    if (result.normalizedName) {
      if (node.type === 'tool' && node.innerActionName) {
        setInnerActionName(result.normalizedName);
      } else if (node.type === 'tool') {
        setToolName(result.normalizedName);
      } else {
        setNodeName(result.normalizedName);
      }
    }

    handleCancelEdit();
  }, [node, editName, renameAction, renameTrigger, renameTool, renameToolInnerAction, setNodeName, setToolName, setInnerActionName, handleCancelEdit, dispatchToast, hasUnappliedNodeCode]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== 'Escape') return;
    // Stop propagation so the modal's Dialog doesn't also receive the
    // keystroke (which would close the editor entirely).
    e.preventDefault();
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else {
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  const handleAskOtto = useCallback(() => {
    if (!node || isAiBusy) return;
    const displayName = getEditableName(node);
    queuePrompt(
      `Help me configure the "${displayName}" ${node.type === 'trigger' ? 'trigger' : 'action'}`,
      { type: 'single', nodeNames: [node.name] },
      'node-editor-header',
    );
  }, [node, isAiBusy, queuePrompt]);

  if (!node) return null;

  const agentBadge = agentVariant ? getAgentBadgeTokens(agentVariant) : null;
  const brandColor =
    agentBadge?.background
    ?? connectorInfo?.brandColor
    ?? builtInManifest?.properties.brandColor
    ?? tokens.colorNeutralBackground4;
  // Agents always render the Fluent glyph below — skip the manifest icon so
  // the Foundry / native / coding split stays visible (manifests share one SVG).
  const iconUri = isAgent
    ? undefined
    : connectorInfo?.iconUri ?? builtInManifest?.properties.iconUri;

  // Eyebrow (above name): agents show their variant label; everything else
  // shows the category suffix used by both drawer and modal.
  const categoryEyebrow =
    node.type === 'trigger'
      ? 'Trigger details'
      : node.type === 'tool'
        ? 'Tool details'
        : 'Action details';
  const eyebrowLabel = agentVariant ? getAgentVariantLabel(agentVariant) : categoryEyebrow;
  // Icon tooltip: for connector operations we surface the connector display
  // name (adds specificity — e.g. "SharePoint" while the eyebrow just says
  // "Action details"). For agents and built-ins the eyebrow already conveys
  // the same info, so the tooltip stays empty and Tooltip won't render.
  const iconTooltipLabel = agentVariant ? null : connectorInfo?.displayName ?? null;

  const displayName = getEditableName(node);
  const nameElement = <Text className={styles.nodeName}>{displayName}</Text>;

  const iconTile = (
    <div
      className={styles.iconContainer}
      style={{ backgroundColor: brandColor }}
      aria-hidden="true"
    >
      {isLoading && connectionRef && !connectorInfo ? (
        <Spinner size="tiny" />
      ) : iconUri && !imageError ? (
        <img
          src={iconUri}
          alt=""
          className={styles.connectorIcon}
          onError={() => setImageError(true)}
        />
      ) : agentVariant ? (
        <span
          aria-hidden="true"
          className={mergeClasses(styles.agentIcon, agentIconForeground(agentVariant))}
        >
          <AgentVariantIcon variant={agentVariant} />
        </span>
      ) : (
        <PlayRegular aria-hidden="true" className={styles.defaultIcon} />
      )}
    </div>
  );

  return (
    <div className={styles.header}>
      {/* Icon — decorative for AT (eyebrow carries the accessible label);
          Tooltip surfaces the specific connector name on mouse hover when it
          adds information beyond the eyebrow. */}
      {iconTooltipLabel ? (
        <Tooltip content={iconTooltipLabel} relationship="description" positioning="below">
          {iconTile}
        </Tooltip>
      ) : (
        iconTile
      )}

      {/* Title section */}
      <div className={styles.titleSection}>
        <Text className={styles.eyebrow} data-testid="node-editor-header-eyebrow">
          {eyebrowLabel}
        </Text>
        <div className={styles.nameRow}>
          {isEditing ? (
            <>
              <Field
                className={styles.nameInput}
                {...getNameValidationProps(editError, editName.length)}
              >
                <Input
                  className={styles.nameInputControl}
                  appearance="underline"
                  value={editName}
                  maxLength={NAME_HARD_CAP}
                  onChange={(_, data) => {
                    setEditName(data.value);
                    setEditError(''); // Clear error on edit
                  }}
                  onKeyDown={handleKeyDown}
                  size="medium"
                  autoFocus
                  aria-label="Edit node name"
                  aria-invalid={!!editError}
                  {...(editName.length >= NAME_COUNTER_REVEAL && {
                    contentAfter: (
                      <span
                        className={mergeClasses(
                          styles.nameCounter,
                          getNameCounterClass(styles, editName.length),
                        )}
                        aria-live="polite"
                        aria-label={`${editName.length} of ${NAME_HARD_CAP} characters`}
                      >
                        {editName.length}/{NAME_HARD_CAP}
                      </span>
                    ),
                  })}
                />
              </Field>
              <Tooltip content="Save (Enter)" relationship="label">
                <Button
                  appearance="primary"
                  size="small"
                  icon={<CheckmarkRegular />}
                  onClick={handleSaveEdit}
                  aria-label="Save name"
                />
              </Tooltip>
              <Tooltip content="Cancel (Esc)" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={handleCancelEdit}
                  aria-label="Cancel editing"
                />
              </Tooltip>
            </>
          ) : (
            <>
              {isNameLocked ? nameElement : (
                <button
                  type="button"
                  className={styles.nameButton}
                  onClick={handleStartEdit}
                  aria-label={`Rename ${node.type}, currently ${displayName}`}
                >
                  {nameElement}
                </button>
              )}
              {!isNameLocked && (
                <Tooltip content="Rename" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<EditRegular />}
                    onClick={handleStartEdit}
                    aria-label="Edit node name"
                    className={styles.editButton}
                  />
                </Tooltip>
              )}
              {isReadOnly && (
                <Tooltip
                  content="Switch to draft mode to edit this workflow."
                  relationship="description"
                >
                  <Badge
                    appearance="tint"
                    color="warning"
                    size="small"
                    icon={<LockClosedRegular />}
                    className={styles.readOnlyBadge}
                  >
                    Read-only
                  </Badge>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>

      {/* Window controls */}
      <div className={styles.actions}>
        {variant === 'drawer' && !isReadOnly && <NodeEditorAutosaveIndicator />}
        {!isReadOnly && isScopedEditEnabled && (
          <Tooltip content="Configure with Copilot" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<SparkleRegular />}
              onClick={handleAskOtto}
              disabled={isAiBusy}
              aria-label="Configure with Copilot"
            />
          </Tooltip>
        )}
        {!isReadOnly && isScopedEditEnabled && (
          <Divider vertical className={styles.actionsDivider} />
        )}
        <Tooltip content="Close" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            onClick={closeEditor}
            aria-label="Close editor"
          />
        </Tooltip>
      </div>
    </div>
  );
};

NodeEditorHeader.displayName = 'NodeEditorHeader';
